import { createSlice, isAnyOf } from '@reduxjs/toolkit';
import Cookies from 'js-cookie';
import jwtDecode from 'jwt-decode';
import { ACCESS_TOKEN, REFRESH_TOKEN } from 'utils/constants';
import { logout, refreshToken, login, login2FA } from './actions';
import { confirmEmail } from '../registration/actions';
import { deleteAccountConfirm } from '../userSettings/actions';
import {loginOTP} from "./actions/loginOTP";

interface Authorization {
	id: number;
	status: string;
	tfaToken?: string;
	otp?: Record<string, any>;
	authError: boolean;
	data: Record<string, any>;
}

const OTP_IDENTIFIER = 'OTP_IDENTIFIER';
const OTP_CREDENTIALS = 'OTP_CREDENTIALS';

const initialState = {
	otp: {
		identifier: Cookies.get(OTP_IDENTIFIER) ?? null,
		credentials: Cookies.get(OTP_CREDENTIALS) ?? null,
	}
} as unknown as Authorization;

type TokensResponse = {
	decodedAccessToken: Record<string, any>,
	decodedRefreshToken: Record<string, any>
};

const setTokens = (payload: { [key: string]: any }): TokensResponse => {
	const accessToken = payload.access_token;
	const refreshToken = payload.refresh_token;
	const decodedAccessToken = jwtDecode<any>(refreshToken);
	const decodedRefreshToken = jwtDecode<any>(refreshToken);
	const refreshTokenExpiration = new Date(decodedRefreshToken.exp * 1000);

	Cookies.set(ACCESS_TOKEN, accessToken, { expires: refreshTokenExpiration });
	Cookies.set(REFRESH_TOKEN, refreshToken, { expires: refreshTokenExpiration });

	return {
		decodedAccessToken,
		decodedRefreshToken
	}
};

const authorizationSlice = createSlice({
	name: 'authorization',
	initialState,
	reducers: {
		initAuth(state, { payload }) {
			if (payload?.access_token) {
				state.data = jwtDecode<any>(payload?.access_token);
			}
		},
		setAuthAdmin(state, action) {
			setTokens(action.payload);
		},
		setAuthError(state, action) {
			state.authError = action.payload;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(login.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			if (payload.tfa_is_active) {
				state.tfaToken = payload.tfa_token;
			} else if (payload?.access_token) {
				const { decodedAccessToken } = setTokens(payload);
				state.data = decodedAccessToken;
			} else if (typeof payload?.identifier === 'string') {
				if (payload?.identifier?.length > 0) {
					Cookies.set(OTP_IDENTIFIER, payload?.identifier);
				}
				if (meta.arg?.credentials) {
					Cookies.set(OTP_CREDENTIALS, meta.arg?.credentials);
				}
				state.otp = {
					...payload,
					...meta.arg,
					identifier: payload?.identifier || state.otp?.identifier,
				}
			}
		});
		builder.addCase(login.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})
		builder.addCase(login.rejected, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})

		builder.addCase(refreshToken.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			const { decodedAccessToken } = setTokens(payload);
			state.data = decodedAccessToken;
		});
		builder.addCase(refreshToken.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})
		builder.addCase(refreshToken.rejected, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})

		builder.addCase(login2FA.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			const { decodedAccessToken } = setTokens(payload);
			state.data = decodedAccessToken;
		});
		builder.addCase(login2FA.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})
		builder.addCase(login2FA.rejected, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})

		builder.addCase(confirmEmail.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			const { decodedAccessToken } = setTokens(payload);
			state.data = decodedAccessToken;
		});
		builder.addCase(confirmEmail.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})
		builder.addCase(confirmEmail.rejected, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})

		builder.addCase(loginOTP.fulfilled, (state, action) => {
			const { payload, meta } = action;
			state.status = meta.requestStatus;
			const { decodedAccessToken } = setTokens(payload);
			state.data = decodedAccessToken;
		});
		builder.addCase(loginOTP.pending, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})
		builder.addCase(loginOTP.rejected, (state, action) => {
			const { meta } = action;
			state.status = meta.requestStatus;
		})

		builder.addMatcher(isAnyOf(logout, deleteAccountConfirm.fulfilled), () => {
			Cookies.remove(ACCESS_TOKEN);
			Cookies.remove(REFRESH_TOKEN);
			return initialState;
		});
	},
});

export const { setAuthAdmin, setAuthError, initAuth } = authorizationSlice.actions;

export default authorizationSlice.reducer;
